import {
  IconName,
  MarketTypeEnum,
  PositionAssetTypeEnum,
  PositionUpdateSourceEnum,
  isTalosUser,
  removeEmptyFilters,
  useBasePairFilter,
  useCurrencyOfSymbolFilter,
  useDerivativesSymbolsFilter,
  useDynamicCallback,
  useMarketsContext,
  usePositionAssetTypesFilter,
  type FilterClause,
  type FilterableProperty,
  type MarketAccount,
  type UseFilterBuilderProps,
} from '@talos/kyoko';
import { compact, isEqual, startCase } from 'lodash-es';
import { useCallback, useMemo } from 'react';
import { useFeatureFlag, useUser } from '../../../../hooks';
import { useDerivativeExpiriesFilter } from '../../../../hooks/filters/useDerivativeExpiriesFilter';
import { useMarketAccountsFilter } from '../../../../hooks/filters/useMarketAccountsFilter';
import type { PositionsTableFilter } from '../types';
import { usePositionsBaseFilter, type UsePositionsBaseFilterParams } from '../usePositionsBaseFilter';

interface UsePositionsDerivativesFilterParams extends UsePositionsBaseFilterParams {
  defaultFilter: PositionsTableFilter;
}

export const DERIVATIVES_PRODUCT_TYPES = [
  PositionAssetTypeEnum.Future,
  PositionAssetTypeEnum.PerpetualSwap,
  PositionAssetTypeEnum.Option,
  PositionAssetTypeEnum.CFD,
];

export const getDerivativesProductTypes = (enablePredictionMarkets: boolean): PositionAssetTypeEnum[] =>
  enablePredictionMarkets
    ? [...DERIVATIVES_PRODUCT_TYPES, PositionAssetTypeEnum.PredictionOutcome]
    : DERIVATIVES_PRODUCT_TYPES;

export const usePositionsDerivativesFilter = ({ defaultFilter, ...params }: UsePositionsDerivativesFilterParams) => {
  const { marketsByName } = useMarketsContext();
  const { enablePredictionMarkets } = useFeatureFlag();
  const user = useUser();

  const baseFilter = usePositionsBaseFilter(params);

  const derivativesSymbolsFilter = useDerivativesSymbolsFilter();
  const currencyOfSymbolFilter = useCurrencyOfSymbolFilter();
  const basePairFilter = useBasePairFilter();
  const expiriesFilter = useDerivativeExpiriesFilter();
  const positionAssetTypesFilter = usePositionAssetTypesFilter({ includePredictionMarkets: enablePredictionMarkets });
  const derivativesProductTypes = useMemo(
    () => getDerivativesProductTypes(enablePredictionMarkets),
    [enablePredictionMarkets]
  );

  const isMarketAccountInExchange = useDynamicCallback((ma: MarketAccount) => {
    return marketsByName.get(ma.Market)?.Type === MarketTypeEnum.Exchange;
  });
  const accountsFilter = useMarketAccountsFilter({ customOptionsFilter: isMarketAccountInExchange });

  const filterableProperties = useMemo(
    () =>
      compact<FilterableProperty<keyof PositionsTableFilter>>([
        { ...derivativesSymbolsFilter, key: 'Symbols' },
        currencyOfSymbolFilter,
        basePairFilter,
        accountsFilter,
        expiriesFilter,
        {
          ...positionAssetTypesFilter,
          options: derivativesProductTypes,
        },
        // Its helpful for engineers to be able to flip this Source switch, but clients shouldn't be able to see it.
        isTalosUser(user) && {
          key: 'Source',
          label: 'Source',
          icon: IconName.CubeTransparent,
          control: 'single-select',
          options: Object.keys(PositionUpdateSourceEnum),
          getOptionLabel: (option: string) => startCase(option),
        },
      ]),
    [
      derivativesSymbolsFilter,
      currencyOfSymbolFilter,
      basePairFilter,
      accountsFilter,
      expiriesFilter,
      user,
      positionAssetTypesFilter,
      derivativesProductTypes,
    ]
  );

  const { changeFilter, initialFilterClauses } = baseFilter;

  const handleFilterClausesChanged = useCallback(
    (filterClausesByPropertyKey: Map<string, FilterClause>, propertiesByKey: Map<string, FilterableProperty>) => {
      changeFilter(curr => {
        const newFilter = removeEmptyFilters<PositionsTableFilter>({
          ...curr,
          // Talos users can set this field, end users cannot.
          ...(Object.fromEntries(
            [...propertiesByKey.keys()].map(key => [key, filterClausesByPropertyKey.get(key)?.selections])
          ) satisfies PositionsTableFilter),
          Source: isTalosUser(user)
            ? (filterClausesByPropertyKey.get('Source')?.selections?.[0] as PositionUpdateSourceEnum)
            : PositionUpdateSourceEnum.Gateway,
        });
        if (isEqual(curr, newFilter)) {
          return curr;
        }
        return newFilter;
      });
    },
    [changeFilter, user]
  );

  const filterBuilderProps = useMemo(
    () =>
      ({
        initialFilterClauses,
        properties: filterableProperties,
        onFilterClausesChanged: handleFilterClausesChanged,
      }) satisfies UseFilterBuilderProps,
    [initialFilterClauses, filterableProperties, handleFilterClausesChanged]
  );

  return {
    filterBuilderProps,
    derivativesProductTypes,
    ...baseFilter,
  };
};

/** Strips client-side-only filter keys and applies derivatives AssetTypes defaults for the WS request. */
export function toDerivativesServerFilter(
  filter: PositionsTableFilter | undefined,
  derivativesProductTypes: PositionAssetTypeEnum[] = DERIVATIVES_PRODUCT_TYPES
): PositionsTableFilter {
  if (filter == null) {
    return {};
  }

  const hasAssetTypeFilter = filter.AssetTypes != null && filter.AssetTypes.length > 0;

  return {
    ...filter,
    Expiries: undefined,
    Currencies: undefined,
    BasePairs: undefined,
    // If the user has deselected all product type filters, load all derivatives product types.
    AssetTypes: hasAssetTypeFilter ? filter.AssetTypes : derivativesProductTypes,
  };
}
